import { hasRole, type ProjectRole } from "../permissions";

export type ClientFinanceAccess = "none" | "support" | "full";

export const getClientFinanceAccess = (roles: readonly ProjectRole[] | null | undefined): ClientFinanceAccess => {
	if (hasRole(roles, "support")) return "support";
	return hasRole(roles, "admin") || hasRole(roles, "accountant") ? "full" : "none";
};

export const canSortClientList = (sort: ClientListSort, access: ClientFinanceAccess): boolean =>
	!sort.startsWith("finance_") || access === "full" ||
	(access === "support" && (sort === "finance_conversion" || sort === "finance_last_activity"));

export type ClientListSort =
	| "created_at" | "name" | "status"
	| "finance_attempts" | "finance_completed" | "finance_failed"
	| "finance_conversion" | "finance_conversion_settled"
	| "finance_turnover" | "finance_revenue" | "finance_profit" | "finance_last_activity";

export interface ClientListRequest {
	with_finance?: 0 | 1;
	with_payout_period?: 0 | 1;
	acquirerId?: number;
	terminalId?: number;
	channelId?: number;
	status?: string | number;
	search?: string;
	page?: number;
	per_page?: number;
	sort?: ClientListSort;
	sort_dir?: "asc" | "desc";
	finance_days?: number;
	currency?: string;
	min_attempts?: number;
	silent?: number;
	conversion_below?: number;
	conversion_above?: number;
}

export const getClientListRequestData = ({ acquirerId, terminalId, channelId, ...filters }: ClientListRequest) => ({
	...filters,
	acquirer_id: acquirerId,
	terminal_id: terminalId,
	channel_id: channelId,
});

// Applied at dispatch so direct API calls and service calls follow the same rule.
export const withClientListFinance = (data: Record<string, unknown>, roles: readonly ProjectRole[] | null | undefined): Record<string, unknown> => {
	const result = { ...data };
	delete result.with_finance;
	// Reference lists explicitly opt out; paginated portfolio requests keep their default.
	if (data.with_finance === 0) return result;
	if (getClientFinanceAccess(roles) !== "none") result.with_finance = 1;
	return result;
};
